import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import AIWarningDialog from '@/components/AIWarningDialog';
import PersistentVideoPlayer from '@/components/PersistentVideoPlayer';
import { celebrateAchievement } from '@/utils/celebrations';
import { trackWorkoutCompleted, trackWorkoutVideoStarted, trackAIChatInteraction } from '@/services/analyticsService';
import { 
  Dumbbell, 
  ArrowLeft,
  Sparkles,
  Loader2,
  Clock,
  Flame,
  Play,
  Check,
  Plus,
  Video,
  Trophy,
  Target,
  Calendar,
  CalendarPlus,
  Award,
  ChevronRight,
  Bot,
  Zap,
  Home,
  Building2,
  Heart,
  Timer,
  Pause,
  X,
  Share2,
  Star,
  BookmarkPlus,
  Search,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Video categories - 20 catégories complètes
const VIDEO_CATEGORIES = [
  { id: 'all', label: 'Toutes', icon: Video },
  { id: 'favorites', label: 'Favoris', icon: Star },
  { id: 'home', label: 'Maison', icon: Home },
  { id: 'gym', label: 'Salle', icon: Building2 },
  { id: 'cardio', label: 'Cardio', icon: Heart },
  { id: 'musculation', label: 'Musculation', icon: Dumbbell },
  { id: 'hiit', label: 'HIIT', icon: Flame },
  { id: 'gainage', label: 'Gainage', icon: Target },
  { id: 'yoga', label: 'Yoga', icon: Heart },
  { id: 'fitness', label: 'Fitness', icon: Zap },
  { id: 'stretching', label: 'Étirements', icon: Timer },
  { id: 'abdos', label: 'Abdos', icon: Target },
  { id: 'jambes', label: 'Jambes', icon: Dumbbell },
  { id: 'bras', label: 'Bras', icon: Dumbbell },
  { id: 'dos', label: 'Dos', icon: Dumbbell },
  { id: 'pectoraux', label: 'Pectoraux', icon: Dumbbell },
  { id: 'pilates', label: 'Pilates', icon: Heart },
  { id: 'boxe', label: 'Boxe', icon: Zap },
  { id: 'danse', label: 'Danse', icon: Video },
  { id: 'debutant', label: 'Débutant', icon: Target },
  { id: 'senior', label: 'Senior', icon: Heart },
  { id: 'postnatal', label: 'Post-Grossesse', icon: Heart },
];

// Difficulty levels
const DIFFICULTY_LABELS = {
  beginner: { label: 'Débutant', color: 'bg-green-500' },
  intermediate: { label: 'Intermédiaire', color: 'bg-yellow-500' },
  expert: { label: 'Expert', color: 'bg-red-500' },
};

// Coach IA program durations
const PROGRAM_DURATIONS = [
  { value: 'week', label: '1 semaine', desc: 'Découverte' },
  { value: '15days', label: '15 jours', desc: 'Court terme' },
  { value: 'month', label: '1 mois', desc: 'Standard' },
  { value: '3months', label: '3 mois', desc: 'Résultats visibles' },
  { value: '6months', label: '6 mois', desc: 'Transformation' },
  { value: 'year', label: '1 an', desc: 'Long terme' },
];

const TIME_OF_DAY = [
  { value: 'morning', label: '🌅 Matin', desc: '6h-10h' },
  { value: 'noon', label: '☀️ Midi', desc: '11h-14h' },
  { value: 'afternoon', label: '🌤️ Après-midi', desc: '15h-18h' },
  { value: 'evening', label: '🌙 Soirée', desc: '19h-22h' },
];

const DAILY_DURATION = [
  { value: '15', label: '15 min', desc: 'Express' },
  { value: '30', label: '30 min', desc: 'Standard' },
  { value: '45', label: '45 min', desc: 'Complet' },
  { value: '60', label: '1h', desc: 'Intensif' },
  { value: '90', label: '1h30', desc: 'Pro' },
];

const BODY_PARTS = [
  { id: 'full_body', label: 'Corps entier', emoji: '💪' },
  { id: 'upper_body', label: 'Haut du corps', emoji: '🏋️' },
  { id: 'lower_body', label: 'Bas du corps', emoji: '🦵' },
  { id: 'abs', label: 'Abdominaux', emoji: '🧘' },
  { id: 'arms', label: 'Bras', emoji: '💪' },
  { id: 'back', label: 'Dos', emoji: '🔙' },
  { id: 'chest', label: 'Pectoraux', emoji: '🫁' },
  { id: 'legs', label: 'Jambes', emoji: '🦿' },
  { id: 'glutes', label: 'Fessiers', emoji: '🍑' },
  { id: 'cardio', label: 'Cardio', emoji: '❤️' },
];

const FITNESS_LEVELS = [
  { value: 'beginner', label: 'Débutant', emoji: '🌱', desc: 'Je commence le sport' },
  { value: 'intermediate', label: 'Intermédiaire', emoji: '💪', desc: 'Je pratique régulièrement' },
  { value: 'advanced', label: 'Avancé', emoji: '🔥', desc: 'Je suis très actif' },
  { value: 'expert', label: 'Expert', emoji: '🏆', desc: 'Sportif confirmé' },
];

const TRAINING_FREQUENCY = [
  { value: '2', label: '2x/semaine', desc: 'Léger' },
  { value: '3', label: '3x/semaine', desc: 'Modéré' },
  { value: '4', label: '4x/semaine', desc: 'Régulier' },
  { value: '5', label: '5x/semaine', desc: 'Intense' },
  { value: '6', label: '6x/semaine', desc: 'Pro' },
];

const TRAINING_GOALS = [
  { id: 'weight_loss', label: 'Perdre du poids', emoji: '⬇️' },
  { id: 'muscle_gain', label: 'Gagner en muscle', emoji: '💪' },
  { id: 'endurance', label: 'Améliorer l\'endurance', emoji: '🏃' },
  { id: 'flexibility', label: 'Gagner en souplesse', emoji: '🧘' },
  { id: 'strength', label: 'Devenir plus fort', emoji: '🏋️' },
  { id: 'toning', label: 'Me tonifier', emoji: '✨' },
  { id: 'health', label: 'Être en meilleure santé', emoji: '❤️' },
  { id: 'stress', label: 'Réduire le stress', emoji: '🧠' },
];

// Congratulation messages for video completion
const CONGRATULATION_MESSAGES = [
  "🎉 Bravo champion ! Tu viens de terminer cette séance avec brio !",
  "💪 Incroyable ! Chaque entraînement te rapproche de ton objectif !",
  "🔥 Tu es en feu ! Continue comme ça, les résultats arrivent !",
  "⭐ Excellent travail ! Tu mérites amplement ce badge !",
  "🚀 Tu as tout donné ! La persévérance est la clé du succès !",
  "🏆 Victoire ! Un entraînement de plus vers la meilleure version de toi !",
  "💯 Parfait ! Ton corps te remercie pour cet effort !",
  "🌟 Superbe performance ! Tu deviens plus fort chaque jour !",
  "🎯 Objectif atteint ! Rien ne peut t'arrêter maintenant !",
  "✨ Magnifique ! Tu inspires ceux qui t'entourent !",
];

export default function WorkoutsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('videos');
  const [loading, setLoading] = useState(true);
  
  // Videos state
  const [videos, setVideos] = useState([]);
  const [videoCategory, setVideoCategory] = useState('all');
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [watchedVideos, setWatchedVideos] = useState([]);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [showCongratsDialog, setShowCongratsDialog] = useState(false);
  const [congratsMessage, setCongratsMessage] = useState('');
  const [earnedBadge, setEarnedBadge] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [favoriteVideos, setFavoriteVideos] = useState([]);
  const [showAddToAgenda, setShowAddToAgenda] = useState(false);
  const [agendaDate, setAgendaDate] = useState('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedShareTarget, setSelectedShareTarget] = useState('public');
  const [videoSearchQuery, setVideoSearchQuery] = useState('');
  const videoRef = useRef(null);
  
  // Persistent Video Player state - video continues even when navigating
  const [isVideoMinimized, setIsVideoMinimized] = useState(false);
  
  // Coach IA state
  const [showCoachDialog, setShowCoachDialog] = useState(false);
  const [coachStep, setCoachStep] = useState(1);
  const [coachLoading, setCoachLoading] = useState(false);
  const [generatedProgram, setGeneratedProgram] = useState(null);
  const [coachConfig, setCoachConfig] = useState({
    duration: 'month',
    frequency: '3',
    fitnessLevel: 'beginner',
    timeOfDay: 'morning',
    dailyDuration: '30',
    bodyParts: [],
    trainingGoals: [],
    equipment: 'none',
    goals: '',
    injuries: '',
    age: '',
    weight: '',
  });
  
  // AI Warning state
  const [showAIWarning, setShowAIWarning] = useState(false);
  
  // Programs & Logs state
  const [programs, setPrograms] = useState([]);
  const [workoutLogs, setWorkoutLogs] = useState([]);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [newLog, setNewLog] = useState({
    workout_name: '',
    duration_minutes: '',
    calories_burned: ''
  });

  // Initial load - only once
  useEffect(() => {
    fetchData();
    fetchVideos();
    loadWatchedVideos();
    loadFavorites();
    loadUserGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Category change - fetch videos only if category changed (not on initial load)
  const initialLoadRef = useRef(true);
  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }
    if (videoCategory !== 'favorites') {
      fetchVideos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoCategory]);

  const fetchData = async () => {
    try {
      const [programsRes, logsRes] = await Promise.allSettled([
        axios.get(`${API}/workouts/programs`, { withCredentials: true }),
        axios.get(`${API}/workouts/logs`, { withCredentials: true })
      ]);
      if (programsRes.status === 'fulfilled') setPrograms(programsRes.value.data || []);
      if (logsRes.status === 'fulfilled') setWorkoutLogs(logsRes.value.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async () => {
    setLoadingVideos(true);
    try {
      // Fetch user profile to get preferences for personalization
      let userPreferences = {};
      try {
        const profileRes = await axios.get(`${API}/profile`, { withCredentials: true });
        const profile = profileRes.data;
        userPreferences = {
          goal: profile.goal, // lose_weight, muscle_gain, maintain
          activity_level: profile.activity_level, // sedentary, light, moderate, active, very_active
          fitness_level: profile.fitness_level,
          gender: profile.gender,
          age: profile.age,
        };
      } catch (e) {
        console.log('Could not fetch profile for personalization');
      }

      const response = await axios.get(`${API}/workouts/videos`, {
        params: { 
          category: videoCategory !== 'all' ? videoCategory : undefined,
          // Pass user preferences for backend personalization
          goal: userPreferences.goal,
          activity_level: userPreferences.activity_level,
        },
        withCredentials: true
      });
      
      let videosData = response.data || [];
      
      // Personalize video order based on user profile
      videosData = personalizeVideos(videosData, userPreferences);
      
      setVideos(videosData);
    } catch (error) {
      console.error('Error fetching videos:', error);
      // Use mock data if API fails and shuffle
      const mockData = getMockVideos();
      const shuffled = [...mockData].sort(() => Math.random() - 0.5);
      setVideos(shuffled);
    } finally {
      setLoadingVideos(false);
    }
  };

  // Personalize videos based on user profile preferences
  const personalizeVideos = (videos, preferences) => {
    if (!preferences || !videos.length) {
      // Just shuffle if no preferences
      return [...videos].sort(() => Math.random() - 0.5);
    }

    // Score each video based on user preferences
    const scoredVideos = videos.map(video => {
      let score = Math.random() * 10; // Base random score for variety
      
      // Boost based on user goal
      if (preferences.goal === 'lose_weight' || preferences.goal === 'lose') {
        if (['cardio', 'hiit', 'fitness'].includes(video.category)) score += 30;
        if (video.calories_burned > 200) score += 20;
      } else if (preferences.goal === 'muscle_gain' || preferences.goal === 'gain') {
        if (['musculation', 'bras', 'jambes', 'dos', 'pectoraux'].includes(video.category)) score += 30;
      } else if (preferences.goal === 'maintain') {
        if (['yoga', 'pilates', 'stretching', 'fitness'].includes(video.category)) score += 20;
      }
      
      // Boost based on activity level
      if (preferences.activity_level === 'sedentary' || preferences.activity_level === 'light') {
        if (video.difficulty === 'beginner' || video.difficulty === 'debutant') score += 25;
        if (['yoga', 'stretching', 'pilates'].includes(video.category)) score += 15;
      } else if (preferences.activity_level === 'very_active' || preferences.activity_level === 'active') {
        if (video.difficulty === 'expert' || video.difficulty === 'intermediate') score += 20;
        if (['hiit', 'musculation', 'boxe'].includes(video.category)) score += 15;
      }
      
      // Adjust for age if available
      if (preferences.age) {
        if (preferences.age > 55 && video.category === 'senior') score += 30;
        if (preferences.age < 30 && ['hiit', 'boxe', 'danse'].includes(video.category)) score += 10;
      }
      
      return { ...video, _score: score };
    });
    
    // Sort by score (highest first) and remove score from output
    return scoredVideos
      .sort((a, b) => b._score - a._score)
      .map(({ _score, ...video }) => video);
  };

  const loadWatchedVideos = () => {
    const saved = localStorage.getItem('watchedWorkoutVideos');
    if (saved) {
      setWatchedVideos(JSON.parse(saved));
    }
  };

  const loadFavorites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/workouts/favorites`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      const favData = response.data || [];
      setFavorites(favData.map(f => f.video_id));
      setFavoriteVideos(favData.map(f => f.video_data).filter(Boolean));
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const loadUserGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/social/user-groups`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUserGroups(response.data?.groups || []);
    } catch (error) {
      console.error('Error loading user groups:', error);
    }
  };

  const toggleFavorite = async (video) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/workouts/favorites`, {
        video_id: video.id,
        video_data: video
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      if (response.data.is_favorite) {
        setFavorites([...favorites, video.id]);
        setFavoriteVideos([...favoriteVideos, video]);
        toast.success('Ajouté aux favoris ⭐');
      } else {
        setFavorites(favorites.filter(id => id !== video.id));
        setFavoriteVideos(favoriteVideos.filter(v => v.id !== video.id));
        toast.info('Retiré des favoris');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour des favoris');
    }
  };

  const addToAgenda = async (video, date) => {
    try {
      const token = localStorage.getItem('token');
      const scheduledDate = date || new Date().toISOString().split('T')[0];
      
      await axios.post(`${API}/workouts/add-to-agenda`, {
        video_data: video,
        scheduled_date: scheduledDate
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Ajouté à l\'agenda 📅');
      setShowAddToAgenda(false);
      setAgendaDate('');
    } catch (error) {
      console.error('Agenda error:', error);
      toast.error('Erreur lors de l\'ajout à l\'agenda');
    }
  };

  const shareWorkout = async (video, caloriesBurned, targetWall = 'public') => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/workouts/share`, {
        video_data: video,
        calories_burned: caloriesBurned,
        target_wall: targetWall
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      const wallName = targetWall === 'public' ? 'le mur public' : 'votre groupe';
      toast.success(`Partagé sur ${wallName} ! +10 points 🎉`);
      setShowShareDialog(false);
    } catch (error) {
      toast.error('Erreur lors du partage');
    }
  };

  const getMockVideos = () => {
    // Fallback videos avec données locales - pas d'URLs externes
    const categories = [
      { id: 'cardio', name: 'Cardio', icon: '❤️', color: '#f43f5e' },
      { id: 'hiit', name: 'HIIT', icon: '🔥', color: '#ef4444' },
      { id: 'musculation', name: 'Musculation', icon: '💪', color: '#8b5cf6' },
      { id: 'yoga', name: 'Yoga', icon: '🧘', color: '#10b981' },
      { id: 'gainage', name: 'Gainage', icon: '🏋️', color: '#0ea5e9' },
      { id: 'home', name: 'Maison', icon: '🏠', color: '#14b8a6' },
      { id: 'gym', name: 'Salle', icon: '🏋️‍♂️', color: '#a855f7' },
      { id: 'abdos', name: 'Abdos', icon: '🎯', color: '#f59e0b' },
      { id: 'jambes', name: 'Jambes', icon: '🦵', color: '#ec4899' },
      { id: 'stretching', name: 'Stretching', icon: '🌿', color: '#22c55e' },
    ];
    
    const mockVideos = [];
    let id = 1;
    
    categories.forEach(cat => {
      for (let i = 0; i < 5; i++) {
        const duration = [15, 20, 25, 30, 35][i % 5];
        const level = ['beginner', 'intermediate', 'expert'][i % 3];
        mockVideos.push({
          id: `mock-${id++}`,
          title: `${cat.name} ${duration} min - Séance ${i + 1}`,
          category: cat.id,
          category_name: cat.name,
          category_icon: cat.icon,
          category_color: cat.color,
          duration: `${duration}:00`,
          duration_minutes: duration,
          level,
          views: Math.floor(Math.random() * 300000) + 50000,
          calories_estimate: duration * 10,
          equipment: ['cardio', 'hiit', 'yoga', 'stretching', 'home', 'abdos'].includes(cat.id) ? 'Aucun' : 'Haltères optionnels',
          description: `Séance de ${cat.name.toLowerCase()} de ${duration} minutes. Niveau ${level}.`,
          instructions: [
            'Échauffement 2-3 minutes',
            'Suivez le rythme indiqué',
            'Hydratez-vous régulièrement',
            'Respirez correctement',
            'Étirements en fin de séance'
          ],
          publishedAt: new Date(Date.now() - (i + 1) * 24 * 60 * 60 * 1000).toISOString(),
        });
      }
    });
    
    return mockVideos;
  };

  // Handle video selection with analytics tracking
  const handleVideoSelect = (video) => {
    trackWorkoutVideoStarted(video.youtube_id || video.id, video.title, video.category || 'fitness');
    setPlayingVideo(video);
  };

  const handleVideoComplete = async (video) => {
    // Add to watched list
    const newWatched = [...watchedVideos, video.id];
    setWatchedVideos(newWatched);
    localStorage.setItem('watchedWorkoutVideos', JSON.stringify(newWatched));
    
    const caloriesBurned = video.calories_estimate || Math.round(video.duration_minutes * 10);
    
    // Track workout completed in Google Analytics
    trackWorkoutCompleted(video.title, video.duration_minutes, caloriesBurned, video.category || 'fitness');
    
    // Generate random congratulation message
    const message = CONGRATULATION_MESSAGES[Math.floor(Math.random() * CONGRATULATION_MESSAGES.length)];
    setCongratsMessage(message);
    
    // Award badge
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/badges/award`, {
        type: 'video_complete',
        video_id: video.id,
        video_title: video.title
      }, { 
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true 
      });
      
      if (response.data.badge) {
        setEarnedBadge(response.data.badge);
      }
    } catch (error) {
      console.error('Error awarding badge:', error);
    }
    
    // Complete workout and update calories burned
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/workouts/complete`, {
        video_data: video,
        calories_burned: caloriesBurned
      }, { 
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true 
      });
      
      toast.success(`🔥 ${caloriesBurned} calories brûlées !`);
      
      // Optionally show share dialog
      setEarnedBadge(prev => ({ 
        ...prev, 
        calories: caloriesBurned,
        totalToday: response.data.total_calories_burned_today 
      }));
    } catch (error) {
      console.error('Error completing workout:', error);
    }
    
    setPlayingVideo(null);
    setShowCongratsDialog(true);
  };

  const generateCoachProgram = async () => {
    setCoachLoading(true);
    try {
      const response = await axios.post(`${API}/workouts/coach/generate`, coachConfig, { withCredentials: true });
      setGeneratedProgram(response.data);
      setCoachStep(7);
      toast.success('Programme généré avec succès !');
    } catch (error) {
      console.error('Error generating program:', error);
      if (error.response?.status === 429) {
        const detail = error.response.data?.detail;
        toast.error(detail?.message || 'Limite quotidienne IA atteinte. Revenez demain !');
      } else {
        toast.error('Erreur lors de la génération du programme');
      }
    } finally {
      setCoachLoading(false);
    }
  };

  // Request coach program with AI warning
  const requestCoachProgram = () => {
    setShowAIWarning(true);
  };

  const handleAIWarningConfirm = () => {
    generateCoachProgram();
  };

  const addProgramToAgenda = async () => {
    if (!generatedProgram) return;
    
    try {
      await axios.post(`${API}/workouts/coach/add-to-agenda`, {
        program: generatedProgram,
        config: coachConfig
      }, { withCredentials: true });
      toast.success('Programme ajouté à votre agenda avec rappels !');
      setShowCoachDialog(false);
      setCoachStep(1);
      setGeneratedProgram(null);
    } catch (error) {
      console.error('Error adding to agenda:', error);
      toast.error('Erreur lors de l\'ajout à l\'agenda');
    }
  };

  const logWorkout = async () => {
    if (!newLog.workout_name || !newLog.duration_minutes) {
      toast.error('Nom et durée requis');
      return;
    }

    try {
      await axios.post(`${API}/workouts/log`, {
        workout_name: newLog.workout_name,
        duration_minutes: parseInt(newLog.duration_minutes),
        calories_burned: parseInt(newLog.calories_burned) || 0,
        exercises: []
      }, { withCredentials: true });
      
      toast.success('Entraînement enregistré !');
      setLogDialogOpen(false);
      setNewLog({ workout_name: '', duration_minutes: '', calories_burned: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const filteredVideos = (videoCategory === 'favorites' 
    ? favoriteVideos 
    : videos.filter(v => videoCategory === 'all' || v.category === videoCategory)
  ).filter(v => {
    // Apply search filter
    if (!videoSearchQuery.trim()) return true;
    const query = videoSearchQuery.toLowerCase();
    return (
      v.title?.toLowerCase().includes(query) ||
      v.category_name?.toLowerCase().includes(query) ||
      v.description?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-safe" data-testid="workouts-page">
      {/* Header */}
      <header className="sticky top-0 z-10 px-4 py-4 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-bold">Entraînements</h1>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowCoachDialog(true)}>
            <Bot className="w-4 h-4 mr-2" />
            Coach IA
          </Button>
        </div>
      </header>

      <main className="p-4 space-y-4 max-w-lg mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="videos" className="flex items-center gap-1 text-xs">
              <Video className="w-4 h-4" />
              Vidéos
            </TabsTrigger>
            <TabsTrigger value="favorites" className="flex items-center gap-1 text-xs">
              <Star className="w-4 h-4" />
              Favoris
            </TabsTrigger>
            <TabsTrigger value="programs" className="flex items-center gap-1 text-xs">
              <Target className="w-4 h-4" />
              Programmes
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-1 text-xs">
              <Clock className="w-4 h-4" />
              Historique
            </TabsTrigger>
          </TabsList>

          {/* Videos Tab */}
          <TabsContent value="videos" className="space-y-4 mt-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher une vidéo..."
                value={videoSearchQuery}
                onChange={(e) => setVideoSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Category Filter - Scrollable horizontal */}
            <div className="relative -mx-4">
              <div 
                className="flex gap-2 px-4 pb-3 overflow-x-auto horizontal-scroll"
                style={{ WebkitOverflowScrolling: 'touch', scrollSnapType: 'x proximity' }}
              >
                {VIDEO_CATEGORIES.map((cat) => (
                  <Button
                    key={cat.id}
                    variant={videoCategory === cat.id ? 'default' : 'outline'}
                    size="sm"
                    className="flex-shrink-0 whitespace-nowrap scroll-snap-align-start"
                    onClick={() => setVideoCategory(cat.id)}
                  >
                    <cat.icon className="w-4 h-4 mr-1" />
                    {cat.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Videos Grid */}
            {loadingVideos ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-3">
                {filteredVideos.map((video) => (
                  <Card 
                    key={video.id}
                    className={`cursor-pointer transition-all hover:border-primary/50 ${
                      watchedVideos.includes(video.id) ? 'border-green-500/50 bg-green-500/5' : ''
                    }`}
                    onClick={() => handleVideoSelect(video)}
                  >
                    <CardContent className="p-3">
                      <div className="flex gap-3">
                        {/* Thumbnail coloré basé sur la catégorie - pas d'image externe */}
                        <div 
                          className="relative flex-shrink-0 w-32 h-20 rounded-lg flex items-center justify-center"
                          style={{
                            background: `linear-gradient(135deg, ${video.category_color || '#6366f1'} 0%, ${video.category_color || '#6366f1'}99 100%)`
                          }}
                        >
                          <span className="text-3xl">{video.category_icon || '🏋️'}</span>
                          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/80 rounded text-xs text-white">
                            {video.duration}
                          </div>
                          {watchedVideos.includes(video.id) && (
                            <div className="absolute top-1 right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm line-clamp-2">{video.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={`text-xs ${DIFFICULTY_LABELS[video.level]?.color}`}>
                              {DIFFICULTY_LABELS[video.level]?.label}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {(video.views / 1000).toFixed(0)}k vues
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            ~{video.calories_estimate || video.duration_minutes * 10} kcal • {video.equipment || 'Sans équipement'}
                          </p>
                        </div>
                        <Button variant="ghost" size="icon" className="self-center">
                          <Play className="w-5 h-5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {filteredVideos.length === 0 && (
                  <div className="text-center py-12">
                    <Video className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Aucune vidéo dans cette catégorie</p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites" className="space-y-4 mt-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Mes vidéos favorites ({favorites.length})
            </h3>
            
            {favorites.length === 0 ? (
              <Card className="p-8 text-center">
                <Star className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">Aucune vidéo favorite</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Cliquez sur l'étoile d'une vidéo pour l'ajouter ici
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {videos.filter(v => favorites.includes(v.id)).map((video) => (
                  <Card 
                    key={video.id}
                    className="cursor-pointer transition-all hover:border-primary/50 border-yellow-500/30"
                    onClick={() => handleVideoSelect(video)}
                  >
                    <CardContent className="p-3">
                      <div className="flex gap-3">
                        <div 
                          className="relative flex-shrink-0 w-32 h-20 rounded-lg flex items-center justify-center"
                          style={{
                            background: `linear-gradient(135deg, ${video.category_color || '#6366f1'} 0%, ${video.category_color || '#6366f1'}99 100%)`
                          }}
                        >
                          <span className="text-3xl">{video.category_icon || '🏋️'}</span>
                          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/80 rounded text-xs text-white">
                            {video.duration}
                          </div>
                          <Star className="absolute top-1 right-1 w-4 h-4 text-yellow-400 fill-yellow-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm line-clamp-2">{video.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={`text-xs ${DIFFICULTY_LABELS[video.level]?.color}`}>
                              {DIFFICULTY_LABELS[video.level]?.label}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            ~{video.calories_estimate || video.duration_minutes * 10} kcal
                          </p>
                        </div>
                        <Button variant="ghost" size="icon" className="self-center">
                          <Play className="w-5 h-5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Programs Tab */}
          <TabsContent value="programs" className="space-y-4 mt-4">
            <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Bot className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">Coach IA Personnel</h3>
                    <p className="text-sm text-muted-foreground">
                      Créez un programme sur mesure adapté à vos objectifs
                    </p>
                  </div>
                  <Button onClick={() => setShowCoachDialog(true)}>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Créer
                  </Button>
                </div>
              </CardContent>
            </Card>

            {programs.length > 0 ? (
              <div className="space-y-3">
                {programs.map((program, index) => (
                  <Card 
                    key={index} 
                    className="cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => setSelectedProgram(program)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">{program.workout_plan?.name || program.workout_plan?.program_name || 'Programme personnalisé'}</h3>
                          <p className="text-sm text-muted-foreground">
                            {program.workout_plan?.weeks?.length || 0} semaines • {program.workout_plan?.weeks?.reduce((acc, w) => acc + (w.days?.length || 0), 0) || 0} séances
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Créé le {new Date(program.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Dumbbell className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Aucun programme créé</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Utilisez le Coach IA pour créer votre premier programme
                </p>
              </div>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4 mt-4">
            <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Enregistrer un entraînement
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Enregistrer un entraînement</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nom de l'entraînement</Label>
                    <Input
                      placeholder="Ex: Cardio HIIT"
                      value={newLog.workout_name}
                      onChange={(e) => setNewLog({ ...newLog, workout_name: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Durée (minutes)</Label>
                      <Input
                        type="number"
                        placeholder="30"
                        value={newLog.duration_minutes}
                        onChange={(e) => setNewLog({ ...newLog, duration_minutes: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Calories brûlées</Label>
                      <Input
                        type="number"
                        placeholder="250"
                        value={newLog.calories_burned}
                        onChange={(e) => setNewLog({ ...newLog, calories_burned: e.target.value })}
                      />
                    </div>
                  </div>
                  <Button onClick={logWorkout} className="w-full">
                    Enregistrer
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {workoutLogs.length > 0 ? (
              <div className="space-y-2">
                {workoutLogs.map((log, index) => (
                  <Card key={index} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => {
                    // If log has video_data, play it
                    if (log.video_data) {
                      setPlayingVideo(log.video_data);
                    }
                  }}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium">{log.workout_name}</p>
                          <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {log.duration_minutes} min
                            </span>
                            <span className="flex items-center gap-1">
                              <Flame className="w-3 h-3" />
                              {log.calories_burned} kcal
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPlayingVideo(log.video_data || { title: log.workout_name, duration_minutes: log.duration_minutes });
                              setShowShareDialog(true);
                            }}
                          >
                            <Share2 className="w-4 h-4" />
                          </Button>
                          <span className="text-xs text-muted-foreground">
                            {new Date(log.logged_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Aucun entraînement enregistré</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Persistent Video Player - Ne se réinitialise jamais */}
      {playingVideo && (
        <PersistentVideoPlayer
          video={playingVideo}
          onClose={() => setPlayingVideo(null)}
          onComplete={() => handleVideoComplete(playingVideo)}
          onFavorite={toggleFavorite}
          onAddToAgenda={(video) => setShowAddToAgenda(true)}
          onShare={(video) => setShowShareDialog(true)}
          isFavorite={favorites.includes(playingVideo.id)}
          isMinimized={isVideoMinimized}
          onMinimizeChange={setIsVideoMinimized}
        />
      )}

      {/* Add to Agenda Dialog */}
      <Dialog open={showAddToAgenda} onOpenChange={setShowAddToAgenda}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>📅 Ajouter à l'agenda</DialogTitle>
            <DialogDescription>
              Planifiez votre entraînement "{playingVideo?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Date prévue</Label>
              <Input 
                type="date" 
                value={agendaDate}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => setAgendaDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddToAgenda(false)}>
              Annuler
            </Button>
            <Button onClick={() => playingVideo && addToAgenda(playingVideo, agendaDate)}>
              <CalendarPlus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog - Choose where to share */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>📤 Partager l'entraînement</DialogTitle>
            <DialogDescription>
              Choisissez où partager "{playingVideo?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {/* Public Wall Option */}
            <Button 
              variant={selectedShareTarget === 'public' ? 'default' : 'outline'}
              className="w-full justify-start h-auto py-3"
              onClick={() => setSelectedShareTarget('public')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  🌍
                </div>
                <div className="text-left">
                  <p className="font-medium">Mur public</p>
                  <p className="text-xs text-muted-foreground">Visible par toute la communauté</p>
                </div>
              </div>
            </Button>
            
            {/* User Groups */}
            {userGroups.length > 0 && (
              <>
                <p className="text-sm font-medium text-muted-foreground mt-4">Mes groupes :</p>
                {userGroups.map(group => (
                  <Button 
                    key={group.group_id}
                    variant={selectedShareTarget === group.group_id ? 'default' : 'outline'}
                    className="w-full justify-start h-auto py-3"
                    onClick={() => setSelectedShareTarget(group.group_id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                        {group.name?.charAt(0) || '👥'}
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{group.name}</p>
                        <p className="text-xs text-muted-foreground">{group.description?.slice(0, 40)}...</p>
                      </div>
                    </div>
                  </Button>
                ))}
              </>
            )}
            
            {userGroups.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-2">
                Rejoignez des groupes pour partager avec eux !
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>
              Annuler
            </Button>
            <Button onClick={() => playingVideo && shareWorkout(playingVideo, playingVideo.calories_estimate || 200, selectedShareTarget)}>
              <Share2 className="w-4 h-4 mr-2" />
              Partager
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Congratulations Dialog */}
      <Dialog open={showCongratsDialog} onOpenChange={setShowCongratsDialog}>
        <DialogContent className="text-center">
          <div className="py-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-primary to-secondary mx-auto flex items-center justify-center mb-4">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <DialogTitle className="text-2xl mb-4">Félicitations !</DialogTitle>
            <p className="text-muted-foreground mb-4">{congratsMessage}</p>
            
            {/* Calories brûlées */}
            {earnedBadge?.calories && (
              <div className="p-4 bg-orange-500/10 rounded-lg mb-4">
                <div className="flex items-center justify-center gap-2 text-orange-600">
                  <Flame className="w-6 h-6" />
                  <span className="text-2xl font-bold">{earnedBadge.calories} kcal</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Total aujourd'hui : {earnedBadge.totalToday || earnedBadge.calories} kcal brûlées
                </p>
              </div>
            )}
            
            {earnedBadge?.name && (
              <div className="p-4 bg-primary/10 rounded-lg mb-4">
                <p className="text-sm text-muted-foreground mb-2">Badge gagné :</p>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-3xl">{earnedBadge.icon}</span>
                  <span className="font-semibold">{earnedBadge.name}</span>
                </div>
              </div>
            )}
            
            {/* Boutons d'action */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  if (playingVideo) shareWorkout(playingVideo, earnedBadge?.calories || 200);
                  setShowCongratsDialog(false);
                }}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Partager
              </Button>
              <Button onClick={() => setShowCongratsDialog(false)} className="flex-1">
                Continuer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Coach IA Dialog - Onboarding amélioré */}
      <Dialog open={showCoachDialog} onOpenChange={(open) => {
        setShowCoachDialog(open);
        if (!open) {
          setCoachStep(1);
          setGeneratedProgram(null);
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" />
              Coach IA - Programme personnalisé
            </DialogTitle>
            <DialogDescription>
              {coachStep < 7 ? `Étape ${coachStep}/6 - Personnalisation avancée` : 'Votre programme est prêt !'}
            </DialogDescription>
          </DialogHeader>

          {/* Progress bar */}
          {coachStep < 7 && (
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300" 
                style={{ width: `${(coachStep / 6) * 100}%` }}
              />
            </div>
          )}

          {/* Step 1: Fitness Level & Goals */}
          {coachStep === 1 && (
            <div className="space-y-4 py-4">
              <div>
                <h3 className="font-semibold mb-3">🎯 Quel est votre niveau actuel ?</h3>
                <div className="grid grid-cols-2 gap-2">
                  {FITNESS_LEVELS.map((level) => (
                    <Button
                      key={level.value}
                      variant={coachConfig.fitnessLevel === level.value ? 'default' : 'outline'}
                      className="h-auto py-3 flex-col"
                      onClick={() => setCoachConfig({ ...coachConfig, fitnessLevel: level.value })}
                    >
                      <span className="text-xl mb-1">{level.emoji}</span>
                      <span className="font-medium">{level.label}</span>
                      <span className="text-xs text-muted-foreground">{level.desc}</span>
                    </Button>
                  ))}
                </div>
              </div>
              <Button className="w-full mt-4" onClick={() => setCoachStep(2)}>
                Suivant
              </Button>
            </div>
          )}

          {/* Step 2: Training Goals */}
          {coachStep === 2 && (
            <div className="space-y-4 py-4">
              <h3 className="font-semibold mb-3">🎯 Quels sont vos objectifs ? (plusieurs choix)</h3>
              <div className="grid grid-cols-2 gap-2">
                {TRAINING_GOALS.map((goal) => (
                  <Button
                    key={goal.id}
                    variant={coachConfig.trainingGoals.includes(goal.id) ? 'default' : 'outline'}
                    className="h-auto py-2 justify-start"
                    onClick={() => {
                      const newGoals = coachConfig.trainingGoals.includes(goal.id)
                        ? coachConfig.trainingGoals.filter(g => g !== goal.id)
                        : [...coachConfig.trainingGoals, goal.id];
                      setCoachConfig({ ...coachConfig, trainingGoals: newGoals });
                    }}
                  >
                    <span className="mr-2">{goal.emoji}</span>
                    {goal.label}
                  </Button>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setCoachStep(1)}>Retour</Button>
                <Button className="flex-1" onClick={() => setCoachStep(3)} disabled={coachConfig.trainingGoals.length === 0}>
                  Suivant
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Duration & Frequency */}
          {coachStep === 3 && (
            <div className="space-y-4 py-4">
              <div>
                <h3 className="font-semibold mb-2">📅 Durée du programme</h3>
                <div className="grid grid-cols-3 gap-2">
                  {PROGRAM_DURATIONS.map((d) => (
                    <Button
                      key={d.value}
                      variant={coachConfig.duration === d.value ? 'default' : 'outline'}
                      className="h-auto py-2 flex-col"
                      size="sm"
                      onClick={() => setCoachConfig({ ...coachConfig, duration: d.value })}
                    >
                      <span className="font-medium text-sm">{d.label}</span>
                      <span className="text-xs text-muted-foreground">{d.desc}</span>
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">🔄 Fréquence d'entraînement</h3>
                <div className="grid grid-cols-5 gap-2">
                  {TRAINING_FREQUENCY.map((f) => (
                    <Button
                      key={f.value}
                      variant={coachConfig.frequency === f.value ? 'default' : 'outline'}
                      size="sm"
                      className="h-auto py-2 flex-col"
                      onClick={() => setCoachConfig({ ...coachConfig, frequency: f.value })}
                    >
                      <span className="font-medium">{f.value}x</span>
                      <span className="text-xs text-muted-foreground">{f.desc}</span>
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setCoachStep(2)}>Retour</Button>
                <Button className="flex-1" onClick={() => setCoachStep(4)}>Suivant</Button>
              </div>
            </div>
          )}

          {/* Step 4: Time & Duration per session */}
          {coachStep === 4 && (
            <div className="space-y-4 py-4">
              <div>
                <h3 className="font-semibold mb-2">⏰ Moment de la journée préféré</h3>
                <div className="grid grid-cols-2 gap-2">
                  {TIME_OF_DAY.map((t) => (
                    <Button
                      key={t.value}
                      variant={coachConfig.timeOfDay === t.value ? 'default' : 'outline'}
                      className="h-auto py-2"
                      onClick={() => setCoachConfig({ ...coachConfig, timeOfDay: t.value })}
                    >
                      <span className="mr-2">{t.label}</span>
                      <span className="text-xs text-muted-foreground">({t.desc})</span>
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">⏱️ Temps par séance</h3>
                <div className="grid grid-cols-5 gap-2">
                  {DAILY_DURATION.map((d) => (
                    <Button
                      key={d.value}
                      variant={coachConfig.dailyDuration === d.value ? 'default' : 'outline'}
                      size="sm"
                      className="h-auto py-2 flex-col"
                      onClick={() => setCoachConfig({ ...coachConfig, dailyDuration: d.value })}
                    >
                      <span className="font-medium">{d.label}</span>
                      <span className="text-xs text-muted-foreground">{d.desc}</span>
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setCoachStep(3)}>Retour</Button>
                <Button className="flex-1" onClick={() => setCoachStep(5)}>Suivant</Button>
              </div>
            </div>
          )}

          {/* Step 5: Body parts */}
          {coachStep === 5 && (
            <div className="space-y-4 py-4">
              <h3 className="font-semibold">🏋️ Parties du corps à travailler</h3>
              <div className="grid grid-cols-2 gap-2">
                {BODY_PARTS.map((part) => (
                  <Button
                    key={part.id}
                    variant={coachConfig.bodyParts.includes(part.id) ? 'default' : 'outline'}
                    className="h-auto py-2 justify-start"
                    onClick={() => {
                      const newParts = coachConfig.bodyParts.includes(part.id)
                        ? coachConfig.bodyParts.filter(p => p !== part.id)
                        : [...coachConfig.bodyParts, part.id];
                      setCoachConfig({ ...coachConfig, bodyParts: newParts });
                    }}
                  >
                    <span className="mr-2">{part.emoji}</span>
                    {part.label}
                  </Button>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setCoachStep(4)}>Retour</Button>
                <Button className="flex-1" onClick={() => setCoachStep(6)} disabled={coachConfig.bodyParts.length === 0}>
                  Suivant
                </Button>
              </div>
            </div>
          )}

          {/* Step 6: Equipment & Additional info */}
          {coachStep === 6 && (
            <div className="space-y-4 py-4">
              <div>
                <h3 className="font-semibold mb-2">🏠 Équipement disponible</h3>
                <Select 
                  value={coachConfig.equipment} 
                  onValueChange={(v) => setCoachConfig({ ...coachConfig, equipment: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun (poids du corps)</SelectItem>
                    <SelectItem value="basic">Basique (haltères, tapis)</SelectItem>
                    <SelectItem value="home">À la maison (élastiques, kettlebell)</SelectItem>
                    <SelectItem value="full">Salle de sport complète</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Âge (optionnel)</Label>
                  <Input
                    type="number"
                    placeholder="Ex: 35"
                    value={coachConfig.age}
                    onChange={(e) => setCoachConfig({ ...coachConfig, age: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-sm">Poids kg (optionnel)</Label>
                  <Input
                    type="number"
                    placeholder="Ex: 70"
                    value={coachConfig.weight}
                    onChange={(e) => setCoachConfig({ ...coachConfig, weight: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>⚠️ Blessures ou limitations (optionnel)</Label>
                <Textarea
                  placeholder="Ex: Problème de genou, douleur au dos..."
                  value={coachConfig.injuries}
                  onChange={(e) => setCoachConfig({ ...coachConfig, injuries: e.target.value })}
                />
              </div>
              <div>
                <Label>💬 Instructions spécifiques (optionnel)</Label>
                <Textarea
                  placeholder="Ex: Je veux inclure du yoga, des étirements le matin..."
                  value={coachConfig.goals}
                  onChange={(e) => setCoachConfig({ ...coachConfig, goals: e.target.value })}
                />
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setCoachStep(5)}>Retour</Button>
                <Button 
                  className="flex-1" 
                  onClick={requestCoachProgram}
                  disabled={coachLoading}
                >
                  {coachLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Génération...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Générer mon programme
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 7: Generated Program */}
          {coachStep === 7 && generatedProgram && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-primary/10 rounded-lg">
                <h3 className="font-semibold text-lg">{generatedProgram.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{generatedProgram.description}</p>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium">Aperçu du programme :</h4>
                <ScrollArea className="h-48">
                  {generatedProgram.weeks?.slice(0, 2).map((week, weekIndex) => (
                    <div key={weekIndex} className="mb-4">
                      <p className="font-medium text-sm text-primary">Semaine {weekIndex + 1}</p>
                      {week.days?.slice(0, 3).map((day, dayIndex) => (
                        <div key={dayIndex} className="ml-4 text-sm py-1 border-l-2 border-primary/30 pl-3">
                          <span className="font-medium">{day.name}:</span> {day.exercises?.length || 0} exercices
                        </div>
                      ))}
                    </div>
                  ))}
                </ScrollArea>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setCoachStep(6)}>
                  Modifier
                </Button>
                <Button className="flex-1" onClick={addProgramToAgenda}>
                  <Calendar className="w-4 h-4 mr-2" />
                  Ajouter à l'agenda
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Program Details Dialog */}
      <Dialog open={!!selectedProgram} onOpenChange={() => setSelectedProgram(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              {selectedProgram?.workout_plan?.name || 'Programme personnalisé'}
            </DialogTitle>
            <DialogDescription>
              {selectedProgram?.workout_plan?.description || 'Votre programme d\'entraînement personnalisé'}
            </DialogDescription>
          </DialogHeader>

          {selectedProgram && (
            <div className="space-y-4 py-4">
              {/* Program Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-primary/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-primary">
                    {selectedProgram.workout_plan?.weeks?.length || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Semaines</p>
                </div>
                <div className="p-3 bg-secondary/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-secondary">
                    {selectedProgram.workout_plan?.weeks?.reduce((acc, w) => acc + (w.days?.length || 0), 0) || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Séances</p>
                </div>
              </div>

              {/* Program Details */}
              <ScrollArea className="h-64">
                {selectedProgram.workout_plan?.weeks?.map((week, weekIndex) => (
                  <div key={weekIndex} className="mb-4">
                    <h4 className="font-semibold text-primary mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Semaine {week.week_number || weekIndex + 1}
                    </h4>
                    {week.days?.map((day, dayIndex) => (
                      <div key={dayIndex} className="ml-4 mb-3 p-3 bg-muted/30 rounded-lg">
                        <p className="font-medium text-sm mb-2">{day.name || `Jour ${dayIndex + 1}`}</p>
                        <div className="space-y-1">
                          {day.exercises?.map((exercise, exIndex) => (
                            <div key={exIndex} className="flex items-center justify-between text-xs">
                              <span>{exercise.name}</span>
                              <span className="text-muted-foreground">
                                {exercise.sets}x{exercise.reps} {exercise.rest && `• ${exercise.rest}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </ScrollArea>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setSelectedProgram(null)}>
                  Fermer
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={() => {
                    addProgramToAgenda();
                    setSelectedProgram(null);
                  }}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Ajouter à l'agenda
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Warning Dialog */}
      <AIWarningDialog
        open={showAIWarning}
        onOpenChange={setShowAIWarning}
        onConfirm={handleAIWarningConfirm}
        title="Génération de programme IA"
        description="Cette fonctionnalité utilise l'intelligence artificielle et consomme un crédit quotidien."
      />
    </div>
  );
}
